import type {
  ActionsAssertion,
  Claim,
  CreativeWorkAssertion,
  ImageProvenance,
} from "@contentauth/sdk";
import { AssertionLabel } from "@contentauth/sdk";
import first from "lodash/first";

export const getProducedBy = (provenance: ImageProvenance, claim: Claim | undefined) => {
  type ProducedBy = {
    name: string;
    organization: string;
    producer: string;
  };

  const getOrganizationAuthor = () => {
    if (!claim) return {};
    const author = (claim.findAssertion(AssertionLabel.CreativeWork) as CreativeWorkAssertion)
      ?.producer;
    if (author?.["@type"] !== "Organization") return {};
    return {
      name: author.name ?? "",
      organization: author.identifier ?? "",
    };
  };

  const getProducer = () => {
    let producer: string | undefined;
    provenance.claims.forEach(claim => {
      type IptcData = {
        "dc:creator": string;
      };
      const metaData = claim.findAssertion("stds.iptc.photo-metadata");
      if (metaData) {
        producer = first((metaData?.data as IptcData)?.["dc:creator"]);
      }
    });
    return producer;
  };

  return {
    name: "",
    organization: "",
    producer: getProducer(),
    ...getOrganizationAuthor(),
  } as ProducedBy;
};

export const getCapturedWith = (provenance: ImageProvenance) => {
  let capturedWith: string | undefined;
  provenance.claims.forEach(claim => {
    type ActionData = {
      actions: { action: string; when: string }[];
    };
    const action = claim.findAssertion(AssertionLabel.Actions) as ActionsAssertion;
    const isCreatedAction = (action?.data as ActionData)?.actions?.find(
      action => action.action === "c2pa.created",
    );
    if (isCreatedAction) {
      capturedWith = claim.data.recorder;
    }
  });
  return capturedWith;
};

/**
 * This function will return the timestamp without time format
 */
export const getCapturedOn = (provenance: ImageProvenance) => {
  let capturedOn: string | undefined;
  provenance.claims.forEach(claim => {
    type SignatureData = {
      ["starling:signatures"]: {
        ["starling:algorithm"]: string;
        ["starling:authenticatedMessagePublic"]: {
          ["starling:assetCreatedTimestamp"]: string;
        };
      }[];
    };
    // cspell: ignore starlinglab
    const integrity = claim.findAssertion("org.starlinglab.integrity");
    const signature = (integrity?.data as SignatureData)?.["starling:signatures"].find(
      signature => signature["starling:algorithm"] === "numbers-Zion",
    );
    capturedOn =
      signature?.["starling:authenticatedMessagePublic"]["starling:assetCreatedTimestamp"];
  });
  return capturedOn;
};

export const getIpfsContentId = (claim: Claim | undefined) => {
  type IpfsData = {
    ["starling:ipfsCID"]: string;
  };
  const ipfsData = claim?.findAssertion("org.starlinglab.storage.ipfs")?.data as IpfsData;
  return ipfsData?.["starling:ipfsCID"];
};

// cspell: ignore filecoin
export const getFileCoin = (claim: Claim | undefined) => {
  type FileCoinData = {
    ["starling:pieceCID"]: string;
    ["starling:provider"]: string;
  };
  const fileCoinData = claim?.findAssertion("org.starlinglab.storage.filecoin")
    ?.data as FileCoinData;
  return {
    id: fileCoinData?.["starling:pieceCID"],
    provider: fileCoinData?.["starling:provider"],
  };
};

export const getFlowNft = (claim: Claim | undefined) => {
  type FlowNftData = {
    ["starling:metadata"]: string;
    ["starling:transaction"]: string;
  };
  const flowNftData = claim?.findAssertion("org.starlinglab.nft.flow")?.data as FlowNftData;
  return {
    id: flowNftData?.["starling:transaction"],
    url: flowNftData?.["starling:metadata"],
  };
};

export const getGpsLocation = (provenance: ImageProvenance) => {
  let location: string | undefined;
  provenance.claims.forEach(claim => {
    type IptcXmpExtensionData = {
      ["Iptc4xmpExt:LocationCreated"]: {
        ["Iptc4xmpExt:City"]?: string;
        ["Iptc4xmpExt:CountryName"]?: string;
        ["Iptc4xmpExt:ProvinceState"]?: string;
      };
    };
    // cspell: ignore starlinglab
    const integrity = claim.findAssertion("stds.iptc.photo-metadata");
    const iptcXmpData = (integrity?.data as IptcXmpExtensionData)?.["Iptc4xmpExt:LocationCreated"];
    if (!iptcXmpData) return;
    const city = iptcXmpData["Iptc4xmpExt:City"];
    const state = iptcXmpData["Iptc4xmpExt:ProvinceState"];
    const country = iptcXmpData["Iptc4xmpExt:CountryName"];
    location = `${city ?? ""} ${state && `, ${state}`} ${country && `, ${country}`}`;
  });
  return location;
};

export const getMedias = (claim: Claim | undefined) =>
  (claim?.findAssertion(AssertionLabel.CreativeWork) as CreativeWorkAssertion)?.socialAccounts ??
  [];
